import { froze } from 'helpers/froze';
import { ANIMATION_CLEAR, ANIMATION_REVERT } from 'constants/animations';
/* eslint-disable indent */

import defer from 'lodash/defer';
import { DefineOwnModule, RootMutations, RootActions } from 'stores/type';
import { CLONE_DISTANCE, LIBRARY_ID } from 'constants/drag';
import { DEFAULT_ELEMENT_ID } from 'constants/misc';
import { workspaceElement } from 'models/workspace-element';
import EventsManager from 'managers/events.manager';
import StorageManager, { StorageKey } from 'managers/storage.manager';
import WorkspaceManager from 'managers/workspace.manager';
import { WorkspaceElement } from 'types';
import { generateId } from 'helpers/id';
import { State, Getters, Actions, Mutations } from './types';

const moduleState: State = {
  elements: StorageManager.load(StorageKey.WORKSPACE) || [],
  elementsPrevious: [],
};

const storeModule: DefineOwnModule<
  State,
  Getters,
  Mutations & Pick<RootMutations, 'DRAG_START' | 'LOGGED_IN' | 'DRAGGABLE_HELPER_SET'>,
  Actions & Pick<RootActions, 'historyIntegrity' | 'resetProgress'>
> = {
  state: moduleState,

  getters: {
    workspaceElements: (state) => state.elements,
    workspacePrevious: (state) => state.elementsPrevious,
    workspaceRevertable: (state, getters) => !!state.elementsPrevious.length
      || !!getters.workspaceInactivePrevious.length,
    workspaceElementsExhausted: (_state, getters) => getters.workspaceElements
      .filter(({ elementId }) => getters.elementIsExhausted(elementId)),
  },

  actions: {
    workspaceClearRevertStart({ dispatch, getters }) {
      if (getters.workspaceRevertable) {
        dispatch('workspaceRevertStart');
      } else {
        dispatch('workspaceClearStart');
      }
    },
    workspaceClearStart({ dispatch, getters }) {
      if (!getters.workspaceElements.length && !getters.workspaceExhausted.length) {
        return;
      }

      dispatch('animationChange', ANIMATION_CLEAR);
      EventsManager.emit('WORKSPACE_CLEAR_START');
      froze();
      EventsManager.once('ANIMATION_WORKSPACE_MIDDLE', () => dispatch('workspaceClear'));
    },
    workspaceRevertStart({ dispatch, getters }) {
      if (!getters.workspacePrevious.length && !getters.workspaceInactivePrevious.length) {
        return;
      }

      dispatch('animationChange', ANIMATION_REVERT);
      froze();
      EventsManager.once('ANIMATION_WORKSPACE_MIDDLE', () => dispatch('workspaceRevert'));
    },
    workspaceClear({ commit, getters }) {
      if (!getters.droppablesActive.length) return;

      commit('WORKSPACE_CLEAR', getters.draggableHelperElement!);
      EventsManager.emit('WORKSPACE_CLEAR');
    },
    workspaceRevert({ commit, getters }) {
      if (!getters.workspacePrevious.length) return;

      commit('WORKSPACE_REVERT');
      EventsManager.emit('WORKSPACE_REVERT');
    },

    workspaceRemoveUnderLibrary({ commit, getters }) {
      const { position: { x } } = getters.droppableStaticById(LIBRARY_ID)!;
      const margin = getters.workspaceBoxDimensions.width / 2;
      const minX = x - margin;

      getters.workspaceElements.forEach((element) => {
        const isDragged = element.id === getters.draggedId;
        const hasCorrectPosition = element.position.x <= minX;
        if (isDragged || hasCorrectPosition) return;

        commit('WORKSPACE_ELEMENT_REMOVE', element.id);
      });
    },
    workspaceRemoveExhausted({ commit, getters }) {
      getters.workspaceElementsExhausted.forEach(
        ({ id }) => commit('WORKSPACE_ELEMENT_REMOVE', id),
      );
    },

    workspaceElementClone({ dispatch, commit, getters }, id) {
      const fromElement = getters.droppableById(id);
      if (!fromElement) return;

      const element = { ...fromElement };

      const directions = WorkspaceManager.getCloneDirections(
        fromElement.position,
        getters.workspaceBoxDimensions,
      );

      commit('DRAGGABLE_HELPER_PREPARE', {
        helper: getters.draggableHelperElement,
        elementId: element.elementId,
        position: {
          x: fromElement.position.x + (directions.x * CLONE_DISTANCE * 2),
          y: fromElement.position.y + (directions.y * CLONE_DISTANCE),
        },
      });

      EventsManager.emit('ELEMENT_CLONE', getters.draggableHelperId!);

      commit('DRAGGABLE_HELPER_RESET');
      window.setTimeout(() => dispatch('draggableHelperAdd', true), 50);
    },

    workspaceElementsAdd({ commit }, droppables) {
      commit('WORKSPACE_ELEMENT_ADD', droppables);
    },
    workspaceElementRemove({ commit }, id) {
      commit('WORKSPACE_ELEMENT_REMOVE', id);
    },

    workspaceRemoveMixParents({ getters, dispatch }) {
      if (!getters.mixFather || !getters.mixMother) return;

      const parents = [getters.mixFather.id, getters.mixMother.id];
      parents.forEach((id) => dispatch('workspaceElementRemove', id));
    },
    // @ts-ignore
    workspaceHandleMixResult({ dispatch, getters }, { removeParents = true } = {}) {
      if (removeParents) {
        dispatch('workspaceRemoveMixParents');
      }

      const children = getters.mixChildren;
      const elements = children.map((child) => ({
        ...workspaceElement,
        id: generateId(child.id),
        elementId: child.id,
        position: child.position!,
      }));

      const final: WorkspaceElement[] = [];
      const exhausted: WorkspaceElement[] = [];
      const normal: WorkspaceElement[] = [];
      elements.forEach((data) => {
        const id = data.elementId;
        const element = getters.elementById(id);

        if (!element) return;
        if (element.final) {
          final.push(data);
          return;
        }

        if (getters.elementIsExhausted(id)) {
          exhausted.push(data);
          return;
        }

        normal.push(data);
      });

      if (normal.length) {
        dispatch('workspaceElementsAdd', normal);
      }

      if (final.length) {
        dispatch('workspaceFinalAdd', final);
      }
      if (exhausted.length) {
        dispatch('workspaceExhaustedAdd', exhausted);
      }

      defer(() => dispatch('libraryReload'));
    },

    historyIntegrity({ commit, getters }) {
      getters.workspaceElements.forEach(({ id, elementId }) => {
        if (getters.draggableHelperId === id) return;

        if (!getters.hasInProgress(elementId)) {
          commit('WORKSPACE_ELEMENT_REMOVE', id);
        }
      });
    },
    resetProgress({ commit, getters }) {
      commit('WORKSPACE_RESET', getters.draggableHelperElement);
    },
  },

  mutations: {
    WORKSPACE_CLEAR(state, draggableHelper) {
      const previous: WorkspaceElement[] = [];
      const helper: WorkspaceElement[] = [];
      state.elements.forEach(
        (e) => ((e && e.id === draggableHelper.id) ? helper : previous).push(e),
      );

      state.elementsPrevious = previous;
      state.elements = helper;
    },
    WORKSPACE_REVERT(state) {
      state.elements.push(...state.elementsPrevious);
      if (state.elementsPrevious.length) {
        state.elementsPrevious = [];
      }
    },

    WORKSPACE_ELEMENT_ADD(state, element) {
      if (state.elements.length >= 1 && !!state.elementsPrevious.length) {
        state.elementsPrevious = [];
      }

      const elements: WorkspaceElement[] = [...element];
      state.elements.push(...elements);
    },
    WORKSPACE_ELEMENT_REMOVE(state, id) {
      state.elements = state.elements.filter((e) => e.id !== id);
    },

    WORKSPACE_RESET(state, draggableHelper) {
      let elements: WorkspaceElement[] = [];
      if (draggableHelper) {
        elements = [draggableHelper];
      }

      state.elements = elements;
      state.elementsPrevious = [];
    },
    LOGGED_IN(state) {
      state.elementsPrevious = [];
    },


    DRAG_START(state) {
      if (state.elementsPrevious.length) {
        state.elementsPrevious = [];
      }
    },

    DRAGGABLE_HELPER_SET(state, id) {
      if (!id) return;

      state.elements.push({
        ...workspaceElement,
        id,
        elementId: DEFAULT_ELEMENT_ID,
        position: {
          x: -500,
          y: -500,
        },
      });
    },
    DRAGGABLE_HELPER_PREPARE(_state, { helper, elementId, position }) {
      if (!helper) return;

      helper.elementId = elementId!;
      helper.position = position!;
    },
  },
};

export default storeModule;
